import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { List, Pencil, Trash2, Check, X } from 'lucide-react';

import { Card } from '../../../components/ui/Card.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { ChunksDrawer } from '../rag/ChunksDrawer.jsx';
import { ragApi } from '../../../api/rag.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { fmt } from '../../../lib/formatters.js';

const STATUS_TONE = { ready: 'success', indexing: 'warning', pending: 'warning', failed: 'danger', archived: 'neutral' };

/**
 * Documents created by promoting feedback into the knowledge base (tag
 * `feedback`). View their chunks (and edit/deactivate/delete them via the
 * shared drawer), rename, or remove the whole document.
 */
export function FeedbackDocs() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('rag:upload');
  const canDelete = hasPermission('rag:delete');

  const [chunksDoc, setChunksDoc] = useState(null);
  const [renaming, setRenaming] = useState(null);     // { id, value }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const query = useQuery({
    queryKey: ['rag', 'documents', { tag: 'feedback' }],
    queryFn: () => ragApi.list({ tag: 'feedback', limit: 50 })
  });
  const items = (query.data && query.data.items) || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rag', 'documents'] });
    queryClient.invalidateQueries({ queryKey: ['rag', 'stats'] });
  };

  const renameMutation = useMutation({
    mutationFn: ({ id, title }) => ragApi.update(id, { title }),
    onSuccess: () => { setRenaming(null); invalidate(); toast.success('Documento rinominato.'); },
    onError: (err) => toast.error(err.message || 'Operazione non riuscita.')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => ragApi.remove(id),
    onSuccess: () => { setDeleteTarget(null); invalidate(); toast.success('Documento eliminato.'); },
    onError: (err) => toast.error(err.message || 'Operazione non riuscita.')
  });

  if (query.isLoading) {
    return <div className="flex flex-col gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>;
  }
  if (items.length === 0) {
    return (
      <Card>
        <p className="text-sm text-ink-muted">
          Nessun documento integrato. Promuovi un feedback dalla scheda "Revisione" per crearne uno.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((d) => {
        const isRenaming = renaming && renaming.id === d._id;
        return (
          <Card key={d._id} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {isRenaming ? (
                <div className="flex flex-1 items-center gap-2">
                  <input
                    autoFocus
                    value={renaming.value}
                    onChange={(e) => setRenaming({ id: d._id, value: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') renameMutation.mutate({ id: d._id, title: renaming.value }); if (e.key === 'Escape') setRenaming(null); }}
                    className="flex-1 rounded-md border border-ink bg-surface-elevated px-2 py-1 text-sm text-ink focus:outline-none"
                  />
                  <button type="button" aria-label="Salva" onClick={() => renameMutation.mutate({ id: d._id, title: renaming.value })} className="text-ink-muted hover:text-success"><Check className="h-4 w-4" /></button>
                  <button type="button" aria-label="Annulla" onClick={() => setRenaming(null)} className="text-ink-muted hover:text-danger"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <>
                  <span className="flex-1 truncate text-sm font-medium text-ink">{d.title || 'Senza titolo'}</span>
                  <Badge tone={STATUS_TONE[d.status] || 'neutral'}>{d.status}</Badge>
                  <span className="text-xs text-ink-muted">{d.chunkCount || 0} chunk</span>
                  <span className="text-xs text-ink-faint">{fmt.dateTime(d.indexedAt || d.createdAt)}</span>
                </>
              )}
            </div>

            {!isRenaming && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => setChunksDoc(d)}>
                  <List className="h-3.5 w-3.5" /> Vedi e modifica chunk
                </Button>
                {canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => setRenaming({ id: d._id, value: d.title || '' })}>
                    <Pencil className="h-3.5 w-3.5" /> Rinomina
                  </Button>
                )}
                {canDelete && (
                  <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(d)}>
                    <Trash2 className="h-3.5 w-3.5" /> Elimina
                  </Button>
                )}
              </div>
            )}
          </Card>
        );
      })}

      <ChunksDrawer open={Boolean(chunksDoc)} document={chunksDoc} onClose={() => setChunksDoc(null)} />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        destructive
        title="Elimina documento"
        description={deleteTarget ? `Eliminare "${deleteTarget.title || 'senza titolo'}" dalla Knowledge base? L'azione è irreversibile.` : ''}
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget._id)}
      />
    </div>
  );
}
